import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { parseAdminModeFromClaims } from '../services/adminModeService';
import type { AdminModeInfo } from '../services/adminModeService';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  adminMode: AdminModeInfo;
  refreshAdminMode: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  adminMode: {
    isActive: false,
    type: null,
    expiresAt: null,
    timeRemaining: null,
    isExpired: false,
  },
  refreshAdminMode: async () => { },
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminMode, setAdminMode] = useState<AdminModeInfo>({
    isActive: false,
    type: null,
    expiresAt: null,
    timeRemaining: null,
    isExpired: false,
  });

  // Function to refresh admin mode state from ID token
  const refreshAdminMode = async () => {
    if (!currentUser) {
      setAdminMode({
        isActive: false,
        type: null,
        expiresAt: null,
        timeRemaining: null,
        isExpired: false,
      });
      return;
    }

    try {
      // Force refresh the token to get latest claims
      const tokenResult = await currentUser.getIdTokenResult(true);
      const adminModeInfo = parseAdminModeFromClaims(tokenResult.claims as {
        admin?: boolean | 'system' | 'org';
        admin_expires_ms?: number;
        org_id?: string;
      });
      setAdminMode(adminModeInfo);
    } catch (error) {
      console.error('Error refreshing admin mode:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Update admin mode when user changes
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          const adminModeInfo = parseAdminModeFromClaims(tokenResult.claims as {
            admin?: boolean | 'system' | 'org';
            admin_expires_ms?: number;
            org_id?: string;
          });
          setAdminMode(adminModeInfo);
        } catch (error) {
          console.error('Error getting admin mode:', error);
        }
      } else {
        setAdminMode({
          isActive: false,
          type: null,
          expiresAt: null,
          timeRemaining: null,
          isExpired: false,
        });
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Set up a timer to update admin mode state when it expires
  useEffect(() => {
    if (!adminMode.isActive || !adminMode.timeRemaining) {
      return;
    }

    // Update every second when admin mode is active
    const interval = setInterval(() => {
      if (adminMode.expiresAt) {
        const now = Date.now();
        const timeRemaining = Math.max(0, adminMode.expiresAt - now);
        const isExpired = timeRemaining === 0;

        setAdminMode((prev) => ({
          ...prev,
          timeRemaining,
          isExpired,
          isActive: !isExpired,
        }));

        // Clear interval when expired
        if (isExpired) {
          clearInterval(interval);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [adminMode.isActive, adminMode.expiresAt, adminMode.timeRemaining]);

  const value = {
    currentUser,
    loading,
    adminMode,
    refreshAdminMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
